import { useLogin } from "@/features/auth/auth.hooks";
import { authApi } from "@/features/auth/auth.api";
import { ensureMockAuthSession, isMockAuthEnabled } from "@/features/auth/auth.mock";
import { useAuthStore, waitForAuthHydration } from "@/features/auth/auth.store";
import { oauthApi } from "@/features/oauth";
import { userApi } from "@/features/user/user.api";
import { useUserStore } from "@/features/user/user.store";
import { rootRoute } from "@/routes";
import { defaultClient } from "@/shared/lib/axios";
import { BackButton, TextInputField } from "@/shared/ui/composites";
import { Button, Divider, HStack, Spinner, VStack } from "@/shared/ui/primitives";
import { Icon } from "@/shared/ui/primitives";
import { createRoute, isRedirect, redirect, useNavigate } from "@tanstack/react-router";
import { AxiosError } from "axios";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { OAuthButtons } from "./components/OAuthButtons";
import { createProviderLabelMap, validateLoginForm } from "./login.utils";

async function hydrateOAuthSession(tokens: { accessToken: string; refreshToken: string }) {
	useAuthStore.getState().setTokens(tokens);

	try {
		const me = await userApi.getMe();
		return { accessToken: tokens.accessToken, refreshToken: tokens.refreshToken, me };
	} catch {
		const refreshed = await authApi.refresh({ refreshToken: tokens.refreshToken });
		useAuthStore.getState().setAccessToken(refreshed.accessToken);
		const me = await userApi.getMe();
		return { accessToken: refreshed.accessToken, refreshToken: tokens.refreshToken, me };
	}
}

const Desktop = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const login = useLogin();
	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [emailError, setEmailError] = useState("");
	const [passwordError, setPasswordError] = useState("");
	const autoRedirectAttemptedRef = useRef(false);

	const kakaoOAuthUrl = oauthApi.getProviderUrl("KAKAO");

	useEffect(() => {
		const params = new URLSearchParams(window.location.search);
		const oauthError = params.get("oauthError");
		if (oauthError) {
			const providerLabelMap = createProviderLabelMap(t);
			const provider =
				oauthError in providerLabelMap
					? providerLabelMap[oauthError as keyof typeof providerLabelMap]
					: t("auth.oauthProviderDefault");
			setPasswordError(t("auth.oauthLoginError", { provider }));
			window.history.replaceState({}, document.title, window.location.pathname);
		}
	}, [t]);

	useEffect(() => {
		if (autoRedirectAttemptedRef.current) return;
		autoRedirectAttemptedRef.current = true;

		const params = new URLSearchParams(window.location.search);
		const returnUrl = params.get("returnUrl");
		const queryAccessToken = params.get("accessToken");
		const queryRefreshToken = params.get("refreshToken");
		const stored = useAuthStore.getState();
		const hasAnySession = Boolean(
			queryAccessToken || queryRefreshToken || stored.accessToken || stored.refreshToken,
		);
		if (!hasAnySession) return;

		let cancelled = false;
		void (async () => {
			try {
				if (queryAccessToken && queryRefreshToken) {
					useAuthStore.getState().setTokens({
						accessToken: queryAccessToken,
						refreshToken: queryRefreshToken,
					});
				}

				try {
					const me = await userApi.getMe();
					const current = useAuthStore.getState();
					const finalAccessToken = current.accessToken;
					const finalRefreshToken = current.refreshToken ?? queryRefreshToken;
					if (!finalAccessToken || !finalRefreshToken) {
						return;
					}
					useAuthStore.getState().setAuth({
						accessToken: finalAccessToken,
						refreshToken: finalRefreshToken,
						user: {
							id: me.id,
							email: me.email,
							name: me.name,
							plan: me.plan,
							gyulBalance: me.gyulBalance,
						},
					});
					useUserStore.getState().setMe(me);
				} catch {
					const current = useAuthStore.getState();
					const currentRefreshToken = current.refreshToken ?? queryRefreshToken;
					if (currentRefreshToken) {
						const refreshed = await authApi.refresh({ refreshToken: currentRefreshToken });
						useAuthStore.getState().setAccessToken(refreshed.accessToken);
					} else {
						const { data } = await defaultClient.post<{ accessToken: string }>("/auth/refresh", {});
						useAuthStore.getState().setAccessToken(data.accessToken);
					}
					const me = await userApi.getMe();
					const next = useAuthStore.getState();
					const finalAccessToken = next.accessToken;
					const finalRefreshToken = next.refreshToken ?? currentRefreshToken;
					if (!finalAccessToken || !finalRefreshToken) {
						return;
					}
					useAuthStore.getState().setAuth({
						accessToken: finalAccessToken,
						refreshToken: finalRefreshToken,
						user: {
							id: me.id,
							email: me.email,
							name: me.name,
							plan: me.plan,
							gyulBalance: me.gyulBalance,
						},
					});
					useUserStore.getState().setMe(me);
				}

				window.history.replaceState({}, document.title, window.location.pathname);
				const target =
					typeof returnUrl === "string" && returnUrl.startsWith("/") ? returnUrl : "/";
				if (!cancelled) {
					window.location.replace(target);
				}
			} catch {
				// stay on login when session is actually invalid
			}
		})();

		return () => {
			cancelled = true;
		};
	}, []);

	const handleLogin = () => {
		const returnUrl = new URLSearchParams(window.location.search).get("returnUrl");
		if (isMockAuthEnabled()) {
			ensureMockAuthSession();
			navigate({ to: "/workflow/$id", params: { id: "mock" }, replace: true });
			return;
		}

		const { isEmailValid, isPasswordValid } = validateLoginForm(email, password);
		setEmailError(isEmailValid ? "" : t("auth.emailError"));
		setPasswordError(isPasswordValid ? "" : t("auth.pwErrMinLength"));
		if (!isEmailValid || !isPasswordValid) return;
		login.mutate(
			{ email, password },
			{
				onSuccess: () => {
					if (returnUrl?.startsWith("/")) {
						window.location.assign(returnUrl);
						return;
					}
					navigate({ to: "/", replace: true });
				},
				onError: (err) => {
					if (err instanceof AxiosError) {
						const code = err.response?.data?.code as string | undefined;

						if (code === "EMAIL_NOT_VERIFIED") {
							navigate({ to: "/verify", search: { email, redirect: "/login" }, replace: true });
							return;
						}

						if (code === "OAUTH_CONFLICT") {
							setEmailError(t("auth.emailOAuthConflict"));
							return;
						}
					}

					setPasswordError(t("auth.loginError"));
				},
			},
		);
	};

	return (
		<VStack className="w-full h-full bg-bg-weak px-[20px] md:px-0">
			<BackButton label={t("sitemap.login")} />
			<form
				className="flex-col w-full max-w-[480px] gap-[28px] text-pm-strong"
				onSubmit={(e) => {
					e.preventDefault();
					handleLogin();
				}}
			>
				<Icon name="Logo" width={180} height={48} viewBox="0 0 180 48" />
				<VStack className="w-full gap-[10px]">
					<TextInputField
						label={t("auth.email")}
						placeholder={t("auth.emailPlaceholder")}
						value={email}
						onChange={(e) => {
							setEmail(e.target.value);
							setEmailError("");
						}}
						error={!!emailError}
						errorMessage={emailError}
					/>
					<TextInputField
						label={t("auth.password")}
						placeholder={t("auth.passwordPlaceholder")}
						subLabel={t("auth.passwordForgot")}
						subAction={() => navigate({ to: "/reset-password" })}
						value={password}
						onChange={(e) => {
							setPassword(e.target.value);
							setPasswordError("");
						}}
						type="password"
						error={!!passwordError}
						errorMessage={passwordError}
					/>
				</VStack>
				<Button variant="primary" className="w-full" type="submit" disabled={login.isPending}>
					{login.isPending ? <Spinner size="sm" /> : t("auth.login")}
				</Button>
				<HStack className="w-full gap-[10px] text-sm text-ct-regular font-normal">
					<Divider className="w-full" />
					<span className="shrink-0">{t("auth.or")}</span>
					<Divider className="w-full" />
				</HStack>
				<OAuthButtons kakaoOAuthUrl={kakaoOAuthUrl} />
				<button type="button" onClick={() => navigate({ to: "/signup" })}>
					<HStack className="gap-[6px] text-sm">
						<span className="text-ct-regular font-normal">{t("auth.firstTime")}</span>
						<span className="text-pm-strong font-medium">{t("auth.signup")}</span>
					</HStack>
				</button>
			</form>
		</VStack>
	);
};

export const loginRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/login",
	component: Desktop,
	beforeLoad: async ({ location }) => {
		await waitForAuthHydration();
		const returnUrl = new URLSearchParams(location.searchStr).get("returnUrl");

		if (ensureMockAuthSession()) {
			throw redirect({ to: "/workflow/$id", params: { id: "mock" }, replace: true });
		}

		// OAuth 콜백 토큰 처리
		const params = new URLSearchParams(window.location.search);
		const accessToken = params.get("accessToken");
		const refreshToken = params.get("refreshToken");

		if (accessToken && refreshToken) {
			try {
				const session = await hydrateOAuthSession({ accessToken, refreshToken });
				useAuthStore.getState().setAuth({
					accessToken: session.accessToken,
					refreshToken: session.refreshToken,
					user: {
						id: session.me.id,
						email: session.me.email,
						name: session.me.name,
						plan: session.me.plan,
						gyulBalance: session.me.gyulBalance,
					},
				});
				useUserStore.getState().setMe(session.me);
				window.history.replaceState({}, document.title, window.location.pathname);
			} catch {
				useAuthStore.getState().clear();
				return;
			}
			if (returnUrl?.startsWith("/")) {
				throw redirect({ to: returnUrl, replace: true });
			}
			throw redirect({ to: "/", replace: true });
		}

		// 이미 로그인된 경우
		const token = useAuthStore.getState().accessToken;
		const storedRefreshToken = useAuthStore.getState().refreshToken;
		if (token || storedRefreshToken) {
			try {
				const me = await userApi.getMe();
				const currentAccessToken = useAuthStore.getState().accessToken;
				const currentRefreshToken = useAuthStore.getState().refreshToken;
				if (currentAccessToken && currentRefreshToken) {
					useAuthStore.getState().setAuth({
						accessToken: currentAccessToken,
						refreshToken: currentRefreshToken,
						user: {
							id: me.id,
							email: me.email,
							name: me.name,
							plan: me.plan,
							gyulBalance: me.gyulBalance,
						},
					});
				}
				useUserStore.getState().setMe(me);
				if (returnUrl?.startsWith("/")) {
					throw redirect({ to: returnUrl, replace: true });
				}
				throw redirect({ to: "/", replace: true });
			} catch (error) {
				if (isRedirect(error)) {
					throw error;
				}
				useAuthStore.getState().clear();
				useUserStore.getState().clear();
			}
		}
	},
});
